// Artifact label/markdown sanitizers.
import { collapseWhitespace, isWhitespace } from "./strings";

/**
 * Remove `[blocked]` markers (flexible spacing, optional closing bracket),
 * including any whitespace immediately before the opening bracket.
 */
function stripBlockedMarkers(s: string): string {
  let out = "";
  let i = 0;
  while (i < s.length) {
    if (s[i] === "[") {
      let j = i + 1;
      while (j < s.length && isWhitespace(s[j])) j++;
      if (s.slice(j, j + 7).toLowerCase() === "blocked") {
        j += 7;
        while (j < s.length && isWhitespace(s[j])) j++;
        if (s[j] === "]") j++;
        // Drop the leading whitespace already emitted before the marker.
        let end = out.length;
        while (end > 0 && isWhitespace(out[end - 1])) end--;
        out = out.slice(0, end);
        i = j;
        continue;
      }
    }
    out += s[i];
    i++;
  }
  return out;
}

export function sanitizeArtifactLabel(value: unknown, fallback = "artifact"): string {
  const raw = typeof value === "string" ? value : "";
  // collapseWhitespace folds runs of whitespace into a single space and trims.
  const cleaned = collapseWhitespace(stripBlockedMarkers(raw));
  return cleaned || fallback;
}

/** Trimmed heading text with leading `#`, spaces and surrounding `**` removed. */
function headingText(line: string): string | null {
  let t = line.trim();
  if (!t.startsWith("#")) return null;
  while (t.length && t[0] === "#") t = t.slice(1);
  t = t.trim();
  while (t.startsWith("*")) t = t.slice(1);
  while (t.endsWith("*")) t = t.slice(0, t.length - 1);
  return t.trim();
}

/**
 * Drop a model-authored "Evidence Artifacts" section (heading + its list). It
 * duplicates — partially and inconsistently — the auto-generated Sources strip
 * (which lists every cited artifact as inline chips), so it reads as "evidence
 * shows one, sources shows many". Removed from the heading up to the next
 * heading (or a `---` rule, or end). No regex — plain line scanning.
 */
function stripEvidenceArtifactsSection(s: string): string {
  const lines = s.split("\n");
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const h = headingText(lines[i]);
    if (h !== null && h.toLowerCase() === "evidence artifacts") {
      i++;
      while (i < lines.length) {
        const next = lines[i].trim();
        if (headingText(lines[i]) !== null) break; // next heading
        if (next === "---" || next === "***" || next === "___") { i++; break; } // rule
        i++;
      }
      // Collapse a doubled blank line left behind by the removal.
      while (out.length && out[out.length - 1].trim() === "" && i < lines.length && lines[i].trim() === "") i++;
      continue;
    }
    out.push(lines[i]);
    i++;
  }
  return out.join("\n");
}

export function sanitizeArtifactMarkdown(markdown: unknown): string {
  if (typeof markdown !== "string" || !markdown) return "";
  return stripEvidenceArtifactsSection(stripBlockedMarkers(markdown));
}

/** One `[label](artifact://id)` citation found in report markdown. */
export interface ArtifactCitation {
  id: string;
  /** The link text as the model wrote it (already whitespace-collapsed). */
  label: string;
}

/**
 * Single scan over the markdown that BOTH reduces `[label](artifact://id)` /
 * `[label](#artifact://id)` citations to their plain label AND reports each one it
 * found, in document order.
 *
 * One walker rather than two: the exporter now needs the ids (to build the cited-
 * evidence appendix) from exactly the links the stripper consumed. Two independent
 * scanners would be free to disagree about what counts as a citation, and the
 * appendix would then list evidence the body never cites — or, worse, silently miss
 * the citation the reader is looking for.
 */
function walkArtifactLinks(s: string, onCitation?: (c: ArtifactCitation) => void): string {
  const PREFIX = "artifact://";
  let out = "";
  let i = 0;
  while (i < s.length) {
    if (s[i] === "[") {
      let k = i + 1;
      while (k < s.length && s[k] !== "]" && s[k] !== "\n") k++;
      if (k < s.length && s[k] === "]" && k > i + 1 && s[k + 1] === "(") {
        let q = k + 2;
        if (s[q] === "#") q++;
        if (s.slice(q, q + PREFIX.length) === PREFIX) {
          q += PREFIX.length;
          const urlStart = q;
          while (q < s.length && s[q] !== ")" && !isWhitespace(s[q])) q++;
          if (q > urlStart && s[q] === ")") {
            const label = s.slice(i + 1, k);
            out += label;
            onCitation?.({ id: s.slice(urlStart, q), label: collapseWhitespace(label) });
            i = q + 1;
            continue;
          }
        }
      }
    }
    out += s[i];
    i++;
  }
  return out;
}

/**
 * Strip internal `[label](artifact://id)` / `[label](#artifact://id)` citation
 * links down to their plain label.
 */
function stripArtifactLinks(s: string): string {
  return walkArtifactLinks(s);
}

/**
 * Every artifact cited by the EXPORTED body of this report, deduped by id, first
 * label wins, document order preserved.
 *
 * Scanned AFTER `sanitizeArtifactMarkdown` on purpose: that sanitizer drops the
 * model-authored "Evidence Artifacts" section, and evidence that is not cited by
 * the text the reader receives has no business in that document's appendix.
 */
export function collectArtifactCitations(markdown: unknown): ArtifactCitation[] {
  const seen = new Set<string>();
  const out: ArtifactCitation[] = [];
  walkArtifactLinks(sanitizeArtifactMarkdown(markdown), (c) => {
    if (!c.id || seen.has(c.id)) return;
    seen.add(c.id);
    out.push(c);
  });
  return out;
}

/**
 * Clean markdown for COPY / DOWNLOAD only (never for render): strips internal
 * artifact:// citation links down to their plain label so exported text never
 * leaks the internal scheme. Render paths must keep the links so the
 * CitationChip / inline-editor interceptor can resolve them.
 */
export function exportArtifactMarkdown(markdown: unknown): string {
  return stripArtifactLinks(sanitizeArtifactMarkdown(markdown));
}

/**
 * Whether an artifact's content should be rendered through a GFM markdown
 * renderer (Streamdown/`SkillResultMarkdown`) rather than as a code fence or
 * a `<pre>` dump.
 *
 * Shared by `ArtifactCard.tsx` (legacy overlay path) and `ArtifactSection.tsx`
 * (protocol-v1 workspace viewer) so the two can't drift on which artifacts
 * count as markdown. The Weave v1 `Artifact.render` field
 * (`services/multi-agent/protocol/weave-protocol-v1.d.ts`) is the most direct
 * signal (`"markdown"` is one of six render kinds — diff/code/table/plan/
 * markdown/download); the legacy REST `GET /api/artifacts/{id}` response
 * (`ArtifactContent` in `types.ts`) carries no `render` field at all, only
 * `type` and `language`, so `type` (`plan_doc` / `report` / `markdown` — the
 * v1 `Artifact.type` enum plus the bare `"markdown"` type some producers use)
 * and `language === "markdown"` are the fallback signals for that path.
 */
export function isMarkdownRenderArtifact(opts: {
  type?: string | null;
  language?: string | null;
  render?: string | null;
}): boolean {
  const render = (opts.render || "").toLowerCase();
  if (render === "markdown") return true;
  const type = (opts.type || "").toLowerCase();
  if (type === "plan_doc" || type === "report" || type === "markdown") return true;
  return (opts.language || "").toLowerCase() === "markdown";
}
